import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import Dropdown from "../../input/Dropdown";
import {
  checkPickupKpiExceeded,
  downloadMultipleReports,
  downloadReport,
  reportTypeDropdown, updateJobField,
} from "../../../api/releaseNoteApi";
import { ResizableScrollingTextRenderer } from "../../../components/renderer/StyleRenderer";
import ButtonRenderer from "../../../components/renderer/ButtonRenderer";
import Button from "../../input/Button";
import Input from "../../input/Input";
import LoadingSpinner from "../../LoadingSpinner";
import { toast } from "react-toastify";
import { getByValueAndKey, getDropdownData } from "../../../api/keyvalue";
import { useSelector } from "react-redux";
import { getJobInfo } from "../../../api/finance/addChargesApi";
import JSZip from "jszip";
import { stakeholderConfigByStakeholderAndConfig } from "../../../api/stakeholderapi";
import { CONFIGS } from "../../../const";
import {
  createAndDownloadAdditionalReleaseNote,
  getAdditionalReleaseNoteLocations,
  searchAdditionalReleaseNotes,
  checkAdditionalReleaseNoteConfig,
  downloadAdditionalReleaseNote,
} from "../../../api/additionalReleaseNoteApi";
import Tab from "../../Tab";
import Grid from "../../Grid";
import {demmurrageDropdown} from "../../../api/demmurrageReasonAPI";

const ADDITIONAL_RELEASE_NOTE_TABS = {
  FORM: "selection",
  HISTORY: "history",
};

const baseAdditionalReleaseNoteColumns = [
  {
    headerName: "Job Code",
    field: "jobCode",
    flex: 1,
  },
  {
    headerName: "Request Code",
    field: "reqCode",
    flex: 1,
  },
  {
    headerName: "Location Name",
    field: "locName",
    flex: 1.25,
    cellRenderer: ResizableScrollingTextRenderer,
  },
  {
    headerName: "Seal No",
    field: "sealNo",
    flex: 1.5,
  },
  {
    headerName: "AOD",
    field: "aod",
    flex: 1,
  },
  {
    headerName: "Version",
    field: "version",
    flex: 0.75,
  },
  {
    headerName: "Created By",
    field: "createdBy",
    flex: 1,
  },
  {
    headerName: "Created On",
    field: "createdOn",
    flex: 1.5,
  },
  {
    headerName: "Last Modified By",
    field: "lastModifiedBy",
    flex: 1.2,
  },
  {
    headerName: "Last Modified On",
    field: "lastModifiedOn",
    flex: 1.5,
  },
];

function JobMonitorReportComponent({
  evinv = false,
  downloadMultiple = false,
  selectedJob = {},
  releaseNoteVariant = "standard",
}) {
  const [reportTypeOptions, setReportTypeOptions] = useState([]);
  const [reportType, setReportType] = useState(0); //46
  const [reportName, setReportName] = useState("");
  const [reportSeq, setReportSeq] = useState(0);
  const [companyProfileSeq, setCompanyProfileSeq] = useState(0);
  const [fileTypeOptions, setFileTypeOptions] = useState([]);
  const [fileType, setFileType] = useState(7);
  const [isLoading, setIsLoading] = useState(false); // Add loading state
  const [relNoteJobAodAndSealCheck, setRelNoteJobAodAndSealCheck] =
    useState(false);
  const [ctpatJobAodAndSealCheck, setCtpatJobAodAndSealCheck] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [additionalHistory, setAdditionalHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isAdditionalSealAndAodMandatory, setIsAdditionalSealAndAodMandatory] =
    useState(false);
  const [additionalJobSealNo, setAdditionalJobSealNo] = useState(() => {
    // Don't auto-fill initially, will be handled in useEffect based on report type
    return "";
  });
  const [additionalJobAod, setAdditionalJobAod] = useState(() => {
    // Don't auto-fill initially, will be handled in useEffect based on report type
    return "";
  });

  const [isPickupExceeded, setIsPickupExceeded] = useState(false);
  const [pickupDemurrageReason, setPickupDemurrageReason] = useState("");
  const [pickupReasonOptions, setPickupReasonOptions] = useState([]);
  const [pickupDuration, setPickupDuration] = useState("");

  const moduleCategory = useSelector((state) => state.report.moduleCategory);
  const user = useSelector((state) => state.user);
  const jobInfo = useSelector((state) => state.report.jobInfo);

  const [isOptionsLoading, setIsOptionsLoading] = useState(false);

  const fetchedResponse = useSelector((state) => state.report.fetchedResponse);

  const evInvMultiple = useSelector((state) => state.report.evInvData);

  const IsDifferentTypeSelect = useSelector(
    (state) => state.report.IsDifferentTypeSelect,
  );
  const [activeAdditionalTab, setActiveAdditionalTab] = useState(
    ADDITIONAL_RELEASE_NOTE_TABS.FORM,
  );

  const downloadFunctionRef = useRef();

  // Create the full column configuration with download button
  const additionalReleaseNoteColumns = useMemo(
    () => [
      ...baseAdditionalReleaseNoteColumns,
      {
        headerName: "Action",
        field: "action",
        flex: 0.8,
        cellRenderer: ButtonRenderer,
        cellRendererParams: (params) => ({
          clickHandler: (event) => {
            console.log("Button clicked, event:", event);
            // Prevent event propagation to avoid triggering modal's click outside handler
            if (event) {
              event.stopPropagation();
              event.preventDefault();
            }
            const rowData = params.data;
            console.log("Row data:", rowData);
            if (rowData.jrnType && rowData.jobId && rowData.reqId) {
              console.log("Calling download function");
              // Call the download function from ref
              downloadFunctionRef.current?.(rowData);
            } else {
              console.log("Missing required data for download:", {
                jrnType: rowData.jrnType,
                jobId: rowData.jobId,
                reqId: rowData.reqId,
              });
            }
          },
          label: "Download",
          className: "ui mini blue button",
          style: { padding: "4px 8px", fontSize: "12px" },
        }),
      },
    ],
    [],
  );

  const fetchAdditionalHistory = useCallback(async () => {
    if (releaseNoteVariant !== "additional") {
      setAdditionalHistory([]);
      return;
    }

    setIsHistoryLoading(true);

    const formatDateTime = (value) => {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    try {
      const response = await searchAdditionalReleaseNotes(jobInfo.jobId);
      console.log("Raw API response for additional release notes:", response);
      const notesArray = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : [];
      console.log("Notes array:", notesArray);
      const mappedHistory =
        notesArray?.map((note, index) => {
          console.log(`Note ${index}:`, note);
          return {
            id: note?.jrnId ?? index,
            jobCode: note?.jobCode ?? note?.jrnJob ?? "",
            reqCode: note?.reqCode ?? note?.jrnRequest ?? "",
            locName: note?.locName ?? note?.jrnRlkId ?? "",
            sealNo: note?.jrnSealNo ?? note?.sealNo ?? "",
            aod: note?.jrnAod ?? note?.aod ?? "",
            version: note?.jrnVersion ?? note?.version ?? "",
            createdBy: note?.createdBy ?? "",
            createdOn: formatDateTime(note?.createdDate),
            lastModifiedBy: note?.lastModifiedBy ?? "",
            lastModifiedOn: formatDateTime(note?.lastModifiedDate),
            // Additional fields needed for download
            jrnType: note?.jrnType ?? note?.type ?? "",
            jobId: note?.jrnJobId ?? note?.jobId ?? jobInfo?.jobId ?? "",
            reqId: note?.jrnRequestId ?? note?.requestId ?? note?.reqId ?? "",
            locationId: note?.jrnRlkId ?? note?.locationId ?? note?.locId ?? "",
          };
        }) ?? [];
      setAdditionalHistory(mappedHistory);
    } catch (error) {
      console.error(error);
      setAdditionalHistory([]);
      toast.error("Failed to load additional release note history", {
        autoClose: 5000,
        containerId: "Modal",
      });
    } finally {
      setIsHistoryLoading(false);
    }
  }, [jobInfo?.jobId, releaseNoteVariant]);

  const resetAdditionalForm = useCallback(() => {
    if (releaseNoteVariant !== "additional") {
      return;
    }
    setSelectedLocation("");
    setAdditionalJobSealNo("");
    setAdditionalJobAod("");
  }, [releaseNoteVariant]);

  const downloadExistingReport = useCallback(
    async (rowData) => {
      try {
        console.log("Download function called with rowData:", rowData);
        console.log("Available rowData keys:", Object.keys(rowData || {}));
        console.log("rowData.jrnType:", rowData?.jrnType);
        console.log("rowData.id:", rowData?.id);
        console.log("rowData.jobId:", rowData?.jobId);
        console.log("rowData.reqId:", rowData?.reqId);
        console.log("rowData.sealNo:", rowData?.sealNo);
        console.log("rowData.aod:", rowData?.aod);
        console.log("rowData.locationId:", rowData?.locationId);
        setIsLoading(true);

        // Use the same payload structure as creation, but with data from the row
        const downloadPayload = {
          reportRequest: {
            companyProfileSeq: companyProfileSeq,
            reportSeq: rowData.jrnType || reportSeq, // Use stored jrnType first, fallback to current reportSeq
            reportTypeSeq: fileType,
            reqSeq: rowData.reqId || 0,
            jobId: rowData.jobId || 0,
            eihCode: "",
            bulkInvoiceOrEvId: 0,
            additionalReleaseNote: true,
            additionalJobSealNo: rowData.sealNo || "", // Use sealNo from rowData
            additionalJobAod: rowData.aod || "", // Use aod from rowData
            selectedLocationId: rowData.locationId || "",
          },
          releaseNoteRecord: {
            jobId: rowData.jobId || 0,
            requestId: rowData.reqId || 0,
            locationId: rowData.locationId || "",
            type: 2,
            jrnType: rowData.jrnType || reportSeq, // Use stored jrnType first, fallback to current reportSeq
            locationBased: rowData.locationId ? 1 : 0,
            sealNo: rowData.sealNo || "",
            aod: rowData.aod || "",
            version: rowData.version || 1,
            createdBy: rowData.createdBy || "",
            lastModifiedBy: rowData.lastModifiedBy || "",
            jrnId: rowData.id, // Include the journal ID for the specific record
          },
        };

        console.log("Download payload:", downloadPayload);
        console.log("Payload reportRequest:", downloadPayload.reportRequest);
        console.log(
          "Payload releaseNoteRecord:",
          downloadPayload.releaseNoteRecord,
        );
        const response = await downloadAdditionalReleaseNote(downloadPayload);

        // Handle the response similar to the main download logic
        const toResponseArray = (data) => {
          if (!data) {
            return [];
          }
          if (Array.isArray(data)) {
            return data;
          }
          if (Array.isArray(data.reportResponses)) {
            return data.reportResponses;
          }
          if (Array.isArray(data.reports)) {
            return data.reports;
          }
          if (data.downloadFile) {
            return [data.downloadFile];
          }
          return [data];
        };

        const responses = toResponseArray(response);
        const fileData = await getByValueAndKey("file_type", fileType);

        // Convert base64 string to a Blob
        const base64ToBlob = (base64, contentType) => {
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          return new Blob([byteArray], { type: contentType });
        };

        const resolveBase64Content = (item) => {
          if (!item) {
            return "";
          }
          const source = item.downloadFile ?? item;
          return (
            source?.base64File ?? source?.fileContent ?? source?.document ?? ""
          );
        };

        const resolveFileName = (item, index = 0) => {
          const source = item?.downloadFile ?? item;
          return (
            source?.displayName ??
            source?.fileName ??
            source?.documentName ??
            `release_note_${index + 1}.${fileData?.keyValue1 ?? "pdf"}`
          );
        };

        if (responses.length > 0) {
          const response = responses[0];
          const base64 = resolveBase64Content(response);
          if (!base64) {
            throw new Error(
              "Failed to download release note: empty file response",
            );
          }
          const fileBlob = base64ToBlob(base64, fileData.keyValue2);

          const downloadLink = document.createElement("a");
          const url = URL.createObjectURL(fileBlob);
          downloadLink.href = url;
          downloadLink.download = resolveFileName(response);
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);

          toast.success("Report Downloaded Successfully", {
            autoClose: 5000,
            containerId: "Modal",
          });
        }
      } catch (error) {
        console.error("Error downloading existing report:", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to download report";

        toast.error(message, {
          autoClose: 5000,
          containerId: "Modal",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [companyProfileSeq, fileType, reportSeq],
  );

  // Set the download function in ref so ButtonRenderer can access it
  useEffect(() => {
    downloadFunctionRef.current = downloadExistingReport;
  }, [downloadExistingReport]);

  console.log("Report Type", reportType);

  useEffect(() => {
    (async () => {
      if (selectedJob?.reqCustomerId) {
        const aodJobSealConfig = await stakeholderConfigByStakeholderAndConfig(
          selectedJob?.reqCustomerId,
          CONFIGS.RELEASE_NOTE_JOB_AOD_AND_SEAL_CHECK,
        );
        console.log("aodJobSealConfig", aodJobSealConfig);
        if (aodJobSealConfig) {
          setRelNoteJobAodAndSealCheck(aodJobSealConfig?.scnBoolean);
        }

        const ctpatJobSealConfig =
          await stakeholderConfigByStakeholderAndConfig(
            selectedJob?.reqCustomerId,
            CONFIGS.CTPAT_JOB_AOD_AND_SEAL_CHECK,
          );
        console.log("ctpatJobSealConfig", ctpatJobSealConfig);
        if (ctpatJobSealConfig) {
          setCtpatJobAodAndSealCheck(ctpatJobSealConfig?.scnBoolean);
        }
      }
    })();
  }, [selectedJob?.reqCustomerId]);

  useEffect(() => {
    (async () => {
      if (selectedJob?.jobId && selectedJob?.reqCustomerId) {
        try {
          const pickupResult = await checkPickupKpiExceeded(
              selectedJob.jobId,
              selectedJob.reqCustomerId
          );
          setIsPickupExceeded(pickupResult?.isExceeded ?? false);
          setPickupDuration(pickupResult?.totalPickupDuration ?? "");

          if (pickupResult?.isExceeded) {
            const reasons = await demmurrageDropdown();
            setPickupReasonOptions(reasons);
          }
        } catch (e) {
          console.error(e);
        }
      }
    })();
  }, [selectedJob?.jobId, selectedJob?.reqCustomerId]);

  useEffect(() => {
    if (releaseNoteVariant !== "additional") {
      setIsAdditionalSealAndAodMandatory(false);
      return;
    }

    const resolvedJobId =
      jobInfo?.jobId ?? selectedJob?.jobId ?? fetchedResponse?.jobId ?? 0;

    if (!resolvedJobId) {
      setIsAdditionalSealAndAodMandatory(false);
      return;
    }

    let isSubscribed = true;

    const evaluateConfigRequirement = async () => {
      try {
        const response = await checkAdditionalReleaseNoteConfig(resolvedJobId);
        if (isSubscribed) {
          setIsAdditionalSealAndAodMandatory(Boolean(response));
        }
      } catch (error) {
        console.error(
          "Failed to fetch additional release note mandatory configuration",
          error,
        );
        if (isSubscribed) {
          setIsAdditionalSealAndAodMandatory(false);
        }
      }
    };

    evaluateConfigRequirement();

    return () => {
      isSubscribed = false;
    };
  }, [
    fetchedResponse?.jobId,
    jobInfo?.jobId,
    releaseNoteVariant,
    selectedJob?.jobId,
  ]);

  // const type = fetchedResponse  ? fetchedResponse?.eihSingleBulk : evInvMultiple[0]?.eihsingle_bulk;
  const type =
    fetchedResponse?.eihSingleBulk ??
    evInvMultiple?.[0]?.eihsingle_bulk ??
    null;

  console.log("type", type);
  useEffect(() => {
    (async () => {
      if (moduleCategory) {
        setIsOptionsLoading(true);
        const response = await reportTypeDropdown(moduleCategory);
        const allowedReportNames = [
          "ReleaseNote",
          "ReleaseNoteGeneral",
          "Ctpat",
        ];
        const filteredResponse =
          releaseNoteVariant === "additional"
            ? (response ?? []).filter((item) =>
                allowedReportNames.includes(item.reportName),
              )
            : response ?? [];

        setReportTypeOptions(filteredResponse);

        if (
          releaseNoteVariant === "additional" &&
          !filteredResponse.some((option) => option.value === reportType)
        ) {
          setReportType(0);
          setReportName("");
          setReportSeq(0);
          setCompanyProfileSeq(0);
        }

        const docTypeData = await getDropdownData("file_type", "2");
        setFileTypeOptions(docTypeData);
        setIsOptionsLoading(false);
      }
    })();
  }, [moduleCategory, releaseNoteVariant, reportType]);

  useEffect(() => {
    setActiveAdditionalTab(ADDITIONAL_RELEASE_NOTE_TABS.FORM);
  }, [releaseNoteVariant]);

  useEffect(() => {
    if (
      releaseNoteVariant === "additional" &&
      activeAdditionalTab === ADDITIONAL_RELEASE_NOTE_TABS.HISTORY
    ) {
      fetchAdditionalHistory();
    }
  }, [activeAdditionalTab, fetchAdditionalHistory, releaseNoteVariant]);

  console.log("selectedJob", selectedJob);

  useEffect(() => {
    if (releaseNoteVariant !== "additional") {
      setSelectedLocation("");
      setLocationOptions([]);
      setAdditionalJobSealNo("");
      setAdditionalJobAod("");
      setIsLocationLoading(false);
      return;
    }

    setSelectedLocation("");
    // Don't auto-fill fields on load - only fill when submitting if empty
    setAdditionalJobSealNo("");
    setAdditionalJobAod("");

    if (!selectedJob?.jobId || !user?.username) {
      setLocationOptions([]);
      return;
    }

    let isMounted = true;
    setIsLocationLoading(true);

    (async () => {
      try {
        const locations = await getAdditionalReleaseNoteLocations({
          jobId: selectedJob.jobId,
          username: user?.username ?? "",
        });

        if (!isMounted) {
          return;
        }

        const mappedLocations =
          locations?.map((loc, index) => {
            const locationId =
              loc.rlkId ??
              loc.locationId ??
              loc.locId ??
              loc.id ??
              `loc-${index}`;

            const locationName =
              loc.locationName ??
              loc.rlkName ??
              loc.locName ??
              loc.name ??
              `Location ${index + 1}`;

            return {
              key: locationId,
              value: locationId,
              text: locationName,
            };
          }) ?? [];

        setLocationOptions(mappedLocations);
      } catch (error) {
        console.error(error);
        if (isMounted) {
          setLocationOptions([]);
          toast.error("Failed to load job locations", {
            autoClose: 5000,
            containerId: "Modal",
          });
        }
      } finally {
        if (isMounted) {
          setIsLocationLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    releaseNoteVariant,
    selectedJob?.jobId,
    selectedJob?.jobAod,
    selectedJob?.jobSeal1,
    selectedJob?.jobSeal2,
    selectedJob?.jobSeal3,
    user?.username,
    reportName,
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Set loading state to true
    try {
      if (!reportType || reportType === null) {
        toast.error("Please select report type", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }
      console.log("selectedJob", selectedJob);

      if (
          isPickupExceeded &&
          (reportName === "ReleaseNote" ||
              reportName === "ReleaseNoteGeneral" ||
              reportName === "Ctpat") &&
          !pickupDemurrageReason
      ) {
        toast.error("Pickup Demurrage Reason is required before generating this document.", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }

      const hasValidValue = (value) => {
        if (value === undefined || value === null) {
          return false;
        }

        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed !== "" && trimmed.toUpperCase() !== "N/A";
        }

        const asString = `${value}`.trim();
        return asString !== "" && asString.toUpperCase() !== "N/A";
      };

      // CTPAT validation for Additional Release Notes - only if mandatory
      // Note: For CTPAT, if fields are empty, system will use job's values, so no validation needed
      if (
        false // Disabled CTPAT validation since system handles empty values by using job's AOD and seal numbers
      ) {
        toast.error(
          "Job Seal No and Job AOD are required for CTPAT Additional Release Note",
          {
            autoClose: 5000,
            containerId: "Modal",
          },
        );
        setIsLoading(false);
        return;
        }
       else if (
        false // Disabled CTPAT validation for normal release notes since system handles job values automatically
      ) {
        toast.error("Job CBM, Job PO No and Job Carton are required", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      } else if (
        releaseNoteVariant !== "additional" &&
        (reportName === "ReleaseNote" || reportName === "ReleaseNoteGeneral") &&
        relNoteJobAodAndSealCheck &&
        (!hasValidValue(selectedJob?.jobAod) ||
          ![
            selectedJob?.jobSeal1,
            selectedJob?.jobSeal2,
            selectedJob?.jobSeal3,
          ].some((seal) => hasValidValue(seal)))
      ) {
        toast.error("At least one job seal and job AOD is required", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }

      if (
        releaseNoteVariant === "additional" &&
        (!selectedLocation || `${selectedLocation}`.trim() === "")
      ) {
        toast.error("Please select at least one location", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }

      const jobAodForValidation =
        releaseNoteVariant === "additional"
          ? additionalJobAod
          : selectedJob?.jobAod;

      const jobSealCandidates =
        releaseNoteVariant === "additional"
          ? (additionalJobSealNo ?? "").split(",").map((seal) => seal.trim())
          : [
              selectedJob?.jobSeal1,
              selectedJob?.jobSeal2,
              selectedJob?.jobSeal3,
            ];

      if (
        releaseNoteVariant === "additional" &&
        isSealAndAodMandatory &&
        !hasValidValue(jobAodForValidation)
      ) {
        toast.error("Job AOD is required", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }

      if (
        releaseNoteVariant === "additional" &&
        isSealAndAodMandatory &&
        !jobSealCandidates.some((seal) => hasValidValue(seal))
      ) {
        toast.error("At least one job seal number is required", {
          autoClose: 5000,
          containerId: "Modal",
        });
        setIsLoading(false);
        return;
      }

      // For CTPAT reports, use job's AOD and seal numbers if input fields are empty
      let trimmedAdditionalJobSealNo = (additionalJobSealNo ?? "").trim();
      let trimmedAdditionalJobAod = (additionalJobAod ?? "").trim();

      if (reportName === "Ctpat" && releaseNoteVariant === "additional") {
        // If input fields are empty, use job's values
        if (!trimmedAdditionalJobSealNo) {
          const jobSeals = [
            selectedJob?.jobSeal1,
            selectedJob?.jobSeal2,
            selectedJob?.jobSeal3,
          ]
            .filter(
              (seal) =>
                seal !== undefined &&
                seal !== null &&
                seal !== "" &&
                seal !== "N/A",
            )
            .join(", ");
          trimmedAdditionalJobSealNo = jobSeals;
        }

        if (!trimmedAdditionalJobAod) {
          trimmedAdditionalJobAod =
            selectedJob?.jobAod && selectedJob?.jobAod !== "N/A"
              ? selectedJob.jobAod
              : "";
        }
      }
      const normalizedLocationId =
        releaseNoteVariant === "additional" && selectedLocation
          ? typeof selectedLocation === "string"
            ? selectedLocation.trim()
            : String(selectedLocation).trim()
          : "";

      const toResponseArray = (data) => {
        if (!data) {
          return [];
        }
        if (Array.isArray(data)) {
          return data;
        }
        if (Array.isArray(data.reportResponses)) {
          return data.reportResponses;
        }
        if (Array.isArray(data.reports)) {
          return data.reports;
        }
        if (data.downloadFile) {
          return [data.downloadFile];
        }
        return [data];
      };

      let responses = [];
      if (releaseNoteVariant === "additional") {
        const resolvedJobId =
          jobInfo?.jobId ?? selectedJob?.jobId ?? fetchedResponse?.jobId ?? 0;
        const resolvedRequestId =
          jobInfo?.jobRequest ??
          selectedJob?.jobRequest ??
          selectedJob?.reqSeq ??
          selectedJob?.reqId ??
          fetchedResponse?.reqSeq ??
          0;
        const username = user?.username ?? "";
        const locationBasedFlag = normalizedLocationId ? 1 : 0;

        const additionalPayload = {
          reportRequest: {
            companyProfileSeq,
            reportSeq,
            reportTypeSeq: fileType,
            reqSeq: type === 2 ? 0 : resolvedRequestId,
            jobId: type === 2 ? 0 : resolvedJobId,
            eihCode:
              type === 2 ? "" : evinv ? fetchedResponse?.eihCode ?? "" : "",
            bulkInvoiceOrEvId: type === 2 ? fetchedResponse?.eihId ?? 0 : 0,
            additionalReleaseNote: true,
            additionalJobSealNo: trimmedAdditionalJobSealNo,
            additionalJobAod: trimmedAdditionalJobAod,
            selectedLocationId: normalizedLocationId,
            pickupDemurrageReason: pickupDemurrageReason,
          },
          releaseNoteRecord: {
            jobId: resolvedJobId,
            requestId: resolvedRequestId,
            locationId: normalizedLocationId,
            type: 2,
            jrnType: reportSeq, // Save the correct report type for future downloads
            locationBased: locationBasedFlag,
            sealNo: trimmedAdditionalJobSealNo,
            aod: trimmedAdditionalJobAod,
            version: 1,
            createdBy: username,
            lastModifiedBy: username,
          },
        };

        console.log(
          "Saving additional release note with payload:",
          additionalPayload,
        );
        console.log(
          "Report type being saved (jrnType):",
          reportSeq,
          "Report name:",
          reportName,
        );

        const response =
          await createAndDownloadAdditionalReleaseNote(additionalPayload);
        responses = toResponseArray(response);
        await fetchAdditionalHistory();
        resetAdditionalForm();
      } else if (downloadMultiple !== true) {
        const response = await downloadReport({
          companyProfileSeq: companyProfileSeq,
          reportSeq: reportSeq,
          reportTypeSeq: fileType,
          reqSeq: type === 2 ? 0 : jobInfo?.jobRequest,
          jobId: type === 2 ? 0 : jobInfo?.jobId,
          eihCode: type === 2 ? "" : evinv ? fetchedResponse?.eihCode : "",
          bulkInvoiceOrEvId: type === 2 ? fetchedResponse?.eihId : 0,
          pickupDemurrageReason: pickupDemurrageReason,
        });
        responses = toResponseArray(response);
      } else {
        const jobIdList = [
          ...new Set(evInvMultiple.map((item) => item.eihjob_id)),
        ];
        const eihCodeList = [
          ...new Set(evInvMultiple.map((item) => item.eihcode)),
        ];
        const eihIdList = [...new Set(evInvMultiple.map((item) => item.eihid))];

        const jobInfoList = await Promise.all(
          jobIdList.map(async (jobId) => {
            const result = await getJobInfo(jobId);
            return Array.isArray(result) ? result[0] : result;
          }),
        );
        const reqList = [
          ...new Set(jobInfoList?.map((item) => item?.jobRequest)),
        ];

        const multiResponse = await downloadMultipleReports({
          companyProfileSeq: companyProfileSeq,
          reportSeq: reportSeq,
          reportTypeSeq: fileType,
          reqSeq: type === 2 ? [0] : reqList,
          jobId: type === 2 ? [0] : jobIdList,
          eihCode: type === 2 ? [""] : evinv ? eihCodeList : [""],
          bulkInvoiceOrEvId: type === 2 ? eihIdList : [0],
          pickupDemurrageReason: pickupDemurrageReason,
        });
        responses = toResponseArray(multiResponse);
      }

      if (isPickupExceeded && pickupDemurrageReason) {
        const jobId = jobInfo?.jobId ?? selectedJob?.jobId ?? 0;
        await updateJobField(jobId, pickupDemurrageReason);
      }

      const fileData = await getByValueAndKey("file_type", fileType);

      // for (const response of responses) {
      // Convert base64 string to a Blob
      const base64ToBlob = (base64, contentType) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
      };

      const resolveBase64Content = (item) => {
        if (!item) {
          return "";
        }
        const source = item.downloadFile ?? item;
        return (
          source?.base64File ?? source?.fileContent ?? source?.document ?? ""
        );
      };
      const resolveFileName = (item, index = 0) => {
        const source = item?.downloadFile ?? item;
        return (
          source?.displayName ??
          source?.fileName ??
          source?.documentName ??
          `release_note_${index + 1}.${fileData?.keyValue1 ?? "pdf"}`
        );
      };

      if (responses.length > 1) {
        const zip = new JSZip();
        responses.forEach((response, index) => {
          const base64 = resolveBase64Content(response);
          if (!base64) {
            return;
          }
          const fileBlob = base64ToBlob(base64, fileData.keyValue2);
          zip.file(resolveFileName(response, index), fileBlob);
        });

        zip.generateAsync({ type: "blob" }).then((zipBlob) => {
          const url = URL.createObjectURL(zipBlob);
          const downloadLink = document.createElement("a");
          downloadLink.href = url;
          downloadLink.download = "EV_INV_files.zip";
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
          URL.revokeObjectURL(url);

          toast.success("ZIP Downloaded Successfully", {
            autoClose: 5000,
            containerId: "Modal",
          });
        });
      } else if (responses.length === 1) {
        const response = responses[0];
        const base64 = resolveBase64Content(response);
        if (!base64) {
          throw new Error(
            "Failed to download release note: empty file response",
          );
        }
        const fileBlob = base64ToBlob(base64, fileData.keyValue2);

        const downloadLink = document.createElement("a");
        const url = URL.createObjectURL(fileBlob);
        downloadLink.href = url;
        downloadLink.download = resolveFileName(response);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(url);

        console.log("Extension:", response);
        toast.success("Successfully Downloaded", {
          autoClose: 5000,
          containerId: "Modal",
        });
      }
    } catch (e) {
      const message =
        e?.response?.data?.reportStatusMessage ||
        e?.response?.data?.message ||
        "Report cannot be downloaded.";

      toast.warning(message, {
        autoClose: 5000,
        containerId: "Modal",
      });
      return null;
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  const isAdditionalVariant = releaseNoteVariant === "additional";
  const isSealAndAodMandatory = isAdditionalVariant
    ? isAdditionalSealAndAodMandatory && reportType !== 45
    : relNoteJobAodAndSealCheck && reportType !== 45;

  const selectionContent = (
    <div className="ui form" style={{ maxHeight: "50%" }}>
      <div className="ui segment" style={{ minHeight: "350px" }}>
        <h4 className="ui dividing header">Report File Selection</h4>
        <div className="two fields">
          <Dropdown
            label="Report Type"
            options={reportTypeOptions}
            value={reportType}
            disabled={isOptionsLoading}
            required={true}
            placeholder={isOptionsLoading ? "Loading..." : "Select a Report"}
            onChange={(e, data) => {
              setReportType(data.value);
              const selectedReport = reportTypeOptions.find(
                (option) => option.value === data.value,
              );
              console.log("selectedReport", selectedReport);
              if (selectedReport) {
                setReportName(selectedReport.reportName);
                setReportSeq(selectedReport.reportSeq);
                setCompanyProfileSeq(selectedReport.companyProfileSeq);
                console.log(
                  "Company Profile: ",
                  selectedReport.companyProfileSeq,
                );
              }
            }}
          />
          <Dropdown
            label="File Type"
            options={fileTypeOptions}
            readOnly={true}
            placeholder="File Type"
            value={fileType}
            onChange={(e, data) => {
              setFileType(data.value);
            }}
          />
        </div>
        {isAdditionalVariant && (
          <>
            <h4 className="ui dividing header">Additional Details</h4>
            <div style={{ width: "100%" }}>
              <Dropdown
                label="Locations"
                options={locationOptions}
                value={selectedLocation}
                style={{ width: "100%", height: "5px", marginBottom: "5px" }}
                required={true}
                placeholder={
                  isLocationLoading
                    ? "Loading locations..."
                    : locationOptions.length > 0
                      ? "Select Location"
                      : "No locations available"
                }
                search
                loading={isLocationLoading}
                disabled={isLocationLoading || locationOptions.length === 0}
                onChange={(_, data) => {
                  setSelectedLocation(data.value ?? "");
                }}
              />
            </div>
            <div className="two fields">
              <Input
                label="Job Seal No"
                value={additionalJobSealNo}
                required={isSealAndAodMandatory}
                placeholder="Enter job seal no"
                onChange={(e) => setAdditionalJobSealNo(e.target.value)}
              />
              <Input
                label="Job AOD"
                value={additionalJobAod}
                required={isSealAndAodMandatory}
                placeholder="Enter job AOD"
                onChange={(e) => setAdditionalJobAod(e.target.value)}
              />
            </div>
          </>
        )}

        {isPickupExceeded && (
            <div className="field">
              <Dropdown
                  label="Pickup Demurrage Reason *"
                  placeholder="Select Pickup Demurrage Reason"
                  options={pickupReasonOptions}
                  value={pickupDemurrageReason}
                  onChange={(e, { value }) => setPickupDemurrageReason(value)}
              />
            </div>
        )}
        <div
          className="two fields"
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          <Button
            template="submit"
            onClick={(e) => {
              handleSubmit(e);
            }}
            disabled={IsDifferentTypeSelect}
          />
        </div>
      </div>
    </div>
  );

  const generatedReportsContent = (
    <div
      className="ui segment"
      style={{
        minHeight: "300px",
        maxHeight: "420px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Grid
        heading="Generated Reports"
        config={additionalReleaseNoteColumns}
        content={additionalHistory}
        searchable={false}
        isLoading={isHistoryLoading}
        rowHeight={45}
        style={{ width: "100%" }}
        vrScroll
        gridHeight={300}
        gridOptions={{
          suppressHorizontalScroll: false,
        }}
        storageKey="aggrid:additional-release-note-history:v2"
      />
    </div>
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAdditionalVariant) {
    return selectionContent;
  }

  return (
    <>
      <div className="ui top attached tabular menu">
        <Tab
          label="Generate Release Note"
          onClick={() =>
            setActiveAdditionalTab(ADDITIONAL_RELEASE_NOTE_TABS.FORM)
          }
          active={activeAdditionalTab === ADDITIONAL_RELEASE_NOTE_TABS.FORM}
        />
        <Tab
          label="Generated Reports"
          onClick={() =>
            setActiveAdditionalTab(ADDITIONAL_RELEASE_NOTE_TABS.HISTORY)
          }
          active={activeAdditionalTab === ADDITIONAL_RELEASE_NOTE_TABS.HISTORY}
        />
      </div>
      <div
        className="ui bottom attached segment"
        style={{
          display:
            activeAdditionalTab === ADDITIONAL_RELEASE_NOTE_TABS.FORM
              ? "block"
              : "none",
        }}
      >
        {selectionContent}
      </div>
      <div
        className="ui bottom attached segment"
        style={{
          display:
            activeAdditionalTab === ADDITIONAL_RELEASE_NOTE_TABS.HISTORY
              ? "block"
              : "none",
        }}
      >
        {generatedReportsContent}
      </div>
    </>
  );
}

export default JobMonitorReportComponent;
